import './BetShops.scss';

import { Icon, ListCell, ListView, Loader, Toast, Typography } from '@modulor/react';
import { nanoid } from '@reduxjs/toolkit';
import { useSkeletonTranslation } from 'hooks/useSkeletonTranslation';
import { useEffect, useLayoutEffect, useState } from 'react';
import IntegrationCommunication from 'services/integration-communication';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import type { PaymentProcessMethodInfo } from 'store/models/paymentProcessModels';
import { useGetDepositMethodInfoQuery } from 'store/services/depositAPI';
import { cleanCurrentMethod, currentDepositMethodCurrencySelector } from 'store/slices/deposit';
import { selectedPayGroupIdSelector } from 'store/slices/global';
import { getLayoutType } from 'utils/common';
import { DESKTOP_MODAL_MIN_HEIGHT, LayoutType } from 'utils/constants';

import { useSetHeaderConfig } from '../components/Header/hooks/useSetHeaderConfig';

interface PreparedViewData {
  type?: string;
  style?: string;
  key?: string;
  value?: string;
  options?: {
    key?: string;
    label?: string;
  }[];
  params?: Record<string, unknown>;
}

export const BetShopsDeposit = () => {
  const currency = useAppSelector(currentDepositMethodCurrencySelector);
  const payGroupId = useAppSelector(selectedPayGroupIdSelector);

  const { translate } = useSkeletonTranslation();
  const dispatch = useAppDispatch();
  const [viewDataForRender, setViewDataForRender] = useState<PreparedViewData[]>([]);

  const layoutType = getLayoutType();
  const isMobileLayout = layoutType === LayoutType.mobile;

  useSetHeaderConfig({
    preset: 'betshopDeposit',
  });

  useEffect(
    () => () => {
      dispatch(cleanCurrentMethod());
    },
    [],
  );

  const {
    data: methodInfo,
    isFetching,
    isSuccess,
    isError,
    error,
  } = useGetDepositMethodInfoQuery(
    {
      payGroupId: payGroupId as number,
      currency: currency || '',
    },
    {
      skip: !currency || !payGroupId,
    },
  );

  useEffect(() => {
    if (isSuccess && methodInfo) {
      const { viewData } = (methodInfo as PaymentProcessMethodInfo) || {};
      const updatedViewData: PreparedViewData[] =
        viewData?.map((data) => ({
          ...data,
          options:
            data.options?.map((option) => ({
              key: nanoid(),
              label: option.label || '',
            })) || [],
        })) || [];
      setViewDataForRender(updatedViewData);
    }
  }, [methodInfo, isSuccess]);

  useLayoutEffect(() => {
    if (!isMobileLayout) {
      const { height = 350 } = window.document.getElementById('root')?.getBoundingClientRect() || {};
      setTimeout(() => {
        IntegrationCommunication.sendMessage({
          id: 'frameChangedHeight',
          value: height <= DESKTOP_MODAL_MIN_HEIGHT ? DESKTOP_MODAL_MIN_HEIGHT + 50 : height,
        });
      }, 0);
    }
  }, [isMobileLayout]);

  const renderBetShopsList = () => (
    <div className="content-page-wrapper" key="modal-content">
      {isFetching && <Loader />}
      {isSuccess && !!viewDataForRender.length && (
        <ListView>
          {viewDataForRender.map((data) => (
            <ListCell
              className="list-cell-control"
              key={data.key}
              leftContent={
                <div>
                  <div>
                    <Typography variant="body-regular">{data.key}</Typography>
                  </div>
                  {data.options?.map((option) => (
                    <div key={option.key}>
                      <Typography variant="caption-1-regular">{option.label}</Typography>
                    </div>
                  ))}
                </div>
              }
              icon={<Icon fill="icon-deposit" name="pin_outlined" />}
            />
          ))}
        </ListView>
      )}
    </div>
  );

  return (
    <>
      {isError && (
        <Toast
          variant="error"
          visible={Boolean(isError)}
          text={translate(error.toString())}
          multiline
          rightIcon={<Icon name="close" />}
          duration={2500}
          onHide={() => {}}
        />
      )}
      {renderBetShopsList()}
    </>
  );
};
